// File: src/pages/mentor-park/SkillGapAnalysisPage.jsx
// Description: Scaffold for Skill Gap Analysis

import React, { useContext, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Chip, 
  Stack, 
  LinearProgress, 
  useTheme, 
  useMediaQuery,
  TextField,
  Card,
  CardContent,
  Alert,
  Container
} from '@mui/material';
import { 
  Assessment,
  Person,
  AutoAwesome,
  PictureAsPdf,
  Timeline,
  School,
  Work,
  TrendingUp
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import AIService from '../../services/AIService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createAuthenticatedAPI } from '../../utils/apiClient';

const SkillGapAnalysisPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [bioText, setBioText] = useState('');
  const contentRef = useRef(null);

  // Create authenticated API client
  const api = createAuthenticatedAPI({ user, logout, navigate });

  // Initialize bio with user's actual bio (same pattern as Career Insights)
  React.useEffect(() => {
    if (user && bioText === '') {
      setBioText(user?.bio || '');
    }
  }, [user, bioText]);

  const buildUserProfile = () => ({
    name: user?.name || 'Professional',
    jobTitle: user?.jobTitle || 'Not specified',
    industry: user?.industry || 'General',
    experience: user?.experience || 'Various',
    location: user?.location || 'Global',
    skills: user?.skills || ['Networking', 'Communication'],
    bio: bioText.trim() || 'Professional seeking guidance', // Use editable bio text
    education: user?.education || 'Not specified',
    currentCompany: user?.currentCompany || 'Not specified',
  });

  const handleAnalyze = async () => {
    if (!user || !bioText.trim()) return;
    setLoading(true);
    try {
      const profile = buildUserProfile();
      const analysis = await AIService.getSkillGapAnalysis(profile);
      setResult(analysis);
    } catch (e) {
      console.error('Skill gap analysis failed', e);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch ((urgency || '').toLowerCase()) {
      case 'high': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.text.secondary;
    }
  };

  const handleDownloadReport = async () => {
    if (!contentRef.current) return;
    
    try {
      const node = contentRef.current;
      const canvas = await html2canvas(node, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        height: node.scrollHeight,
        width: node.scrollWidth
      });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Add TouchConnect header
      pdf.setFontSize(20);
      pdf.setTextColor(30, 64, 175); // Primary blue color
      pdf.text('TouchConnect Skill Gap Analysis Report', 40, 40);
      
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated for: ${user?.name || 'Professional'}`, 40, 60);
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, 40, 75);
      
      // Add company branding
      pdf.setFontSize(10);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Powered by TouchConnect - Digital Business Card Platform', 40, pageHeight - 30);
      
      // Add content with margins for header/footer
      const imgWidth = pageWidth - 80; // 40px margins on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let position = 90; // Start after header
      let heightLeft = imgHeight;
      const availableHeight = pageHeight - 130; // Account for header and footer

      pdf.addImage(imgData, 'PNG', 40, position, imgWidth, imgHeight);
      heightLeft -= availableHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        
        // Add header to new page
        pdf.setFontSize(12);
        pdf.setTextColor(30, 64, 175);
        pdf.text('TouchConnect Skill Gap Analysis Report (continued)', 40, 30);
        
        position = 50 - (imgHeight - heightLeft);
        pdf.addImage(imgData, 'PNG', 40, position, imgWidth, imgHeight);
        heightLeft -= availableHeight;
        
        // Add footer to new page
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Powered by TouchConnect - Digital Business Card Platform', 40, pageHeight - 30);
      }

      pdf.save(`TouchConnect_Skill_Gap_Analysis_${user?.name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <Assessment sx={{ fontSize: 40, color: theme.palette.warning.main }} />
          <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Skill Gap Analysis
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mb: 3 }}>
          Advanced skill mapping, gap prioritization, career path scenarios, and cross-sector transferability analysis
        </Typography>
      </Box>

      {/* Bio Input Section */}
      <Card sx={{ mb: 4, maxWidth: '900px', mx: 'auto' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Person sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Your Professional Profile
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and update your professional background below. This information will be used to generate personalized skill gap analysis.
          </Typography>
          
          <TextField
            label="Professional Bio"
            placeholder="Paste your professional bio or describe your background, experience, skills, and career objectives..."
            value={bioText}
            onChange={(e) => setBioText(e.target.value)}
            multiline
            rows={6}
            fullWidth
            variant="outlined"
            helperText={`${bioText.length} characters`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {/* Analyze Button */}
            <Button
              variant="contained"
              startIcon={<AutoAwesome />}
              onClick={handleAnalyze}
              disabled={loading || !user || !bioText.trim()}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(30, 64, 175, 0.4)'
                },
                '&:disabled': {
                  transform: 'none',
                  boxShadow: 'none'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {loading ? 'Analyzing...' : 'Analyse Skill Gaps'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Quick tip */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Alert severity="info" sx={{ maxWidth: '600px', mx: 'auto', borderRadius: 2 }}>
          <Typography variant="body2">
            💡 <strong>Tip:</strong> Include your current skills, experience level, target roles, and industry for the most accurate skill gap analysis.
          </Typography>
        </Alert>
      </Box>

      {/* Download Report Button - Show only when results are available */}
      {result && (
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={handleDownloadReport}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: theme.palette.success.main,
              color: theme.palette.success.main,
              '&:hover': {
                borderColor: theme.palette.success.dark,
                backgroundColor: theme.palette.success.light + '10',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.2)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Download Skill Gap Report
          </Button>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ mb: 4 }}>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            AI is analyzing your skill gaps...
          </Typography>
        </Box>
      )}

      {/* Results */}
      {result && (
        <Box ref={contentRef} sx={{ mt: 3 }}>
          {/* Skill Mapping */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Timeline sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Skill Mapping
                </Typography>
              </Box>
            <Grid container spacing={2}>
              {result.mapping?.map((m, idx) => (
                <Grid key={idx} item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{m.skill}</Typography>
                    <Typography variant="body2" color="text.secondary">Level: {m.level} / Benchmark: {m.benchmark}</Typography>
                    {typeof m.gap === 'number' && (
                      <Typography variant="caption" color="text.secondary">Gap: {m.gap}</Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
            </CardContent>
          </Card>

          {/* Gap Detection & Prioritization */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Assessment sx={{ color: theme.palette.warning.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Gap Detection & Prioritization
                </Typography>
              </Box>
            <Stack spacing={2}>
              {result.prioritizedGaps?.map((g, idx) => (
                <Paper key={idx} sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{g.skill}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label={`Urgency: ${g.urgency}`} size="small" sx={{ bgcolor: getUrgencyColor(g.urgency), color: 'white' }} />
                      <Chip label={`Demand: ${g.marketDemand}`} size="small" variant="outlined" />
                    </Box>
                  </Box>
                  {g.estimatedTime && (
                    <Typography variant="caption" color="text.secondary">Estimated time: {g.estimatedTime}</Typography>
                  )}
                  {Array.isArray(g.recommendedResources) && g.recommendedResources.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {g.recommendedResources.map((r, i) => (
                        <Chip key={i} label={r} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
            </CardContent>
          </Card>

          {/* Career Path Scenarios */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Work sx={{ color: theme.palette.success.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Career Path Scenarios
                </Typography>
              </Box>
            <Stack spacing={2}>
              {result.careerPathScenarios?.map((s, idx) => (
                <Paper key={idx} sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{s.targetRole}</Typography>
                  {s.feasibility && (
                    <Chip label={`Feasibility: ${s.feasibility}`} size="small" sx={{ mt: 1 }} />
                  )}
                  {Array.isArray(s.steps) && s.steps.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {s.steps.map((step, i) => (
                        <Chip key={i} label={step} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
            </CardContent>
          </Card>

          {/* Cross-Sector Transferability */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <TrendingUp sx={{ color: theme.palette.info.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Cross-Sector Transferability
                </Typography>
              </Box>
            <Grid container spacing={2}>
              {result.transferableSkills?.map((t, idx) => (
                <Grid key={idx} item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.skill}</Typography>
                    <Typography variant="body2" color="text.secondary">Transferable to: {t.transferableTo}</Typography>
                    {t.applicability && (
                      <Chip label={`Applicability: ${t.applicability}`} size="small" sx={{ mt: 1 }} />
                    )}
                    {t.note && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t.note}</Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default SkillGapAnalysisPage;


